import Handsontable from "handsontable";
import {VZHotTableConfig} from "../../vz-ui/vz-hot-table/vz-hot-table-config";
import {vzHotTableCells} from "../../vz-ui/vz-hot-table/vz-hot-table-helpers";
import {ColumnSettings} from "handsontable/settings";
import {MutableRefObject} from "react";
import {RenovationsPackageTableRow} from "../AnalystRenovationDrawer";
import * as css from "../styles/renovations-drawer.module.scss";
import {Close} from "@material-ui/icons";
import ReactDOMServer from 'react-dom/server';
import { v4 as uuid } from "uuid";


interface CostCategoryMapping {
    costCategoryId: string;
    amount: string | number | null;
    delayRecognition: string | number | null;
}

type PackageUpdateMap = Map<string, {
    id: string;
    name: string;
    premium: string | null;
    premiumDelayRecognition: string | null;
    costCategoryMappings: CostCategoryMapping[];
}>

interface PackageChangesReturn {
    packagesToCreate: PackageUpdateMap;
    packagesToUpdate: PackageUpdateMap;
    packagesToDelete: Set<string>;
}

// static svg icon html, for use in the remove package button since Handsontable
// does not support JSX without using the HotColumn component, and HotColumn does not
// work well with dynamically adding and removing columns
const svgIconClose = ReactDOMServer.renderToStaticMarkup(<Close />);
export const packageRemovalButtonMarkup = `<button class='${css.removePackageButton}'>${svgIconClose}</button>`;

// tells handsontable what data belongs in which columns
export function buildColumnConfig(tableData: RenovationsPackageTableRow[]): ColumnSettings[] {
    const colLength = (tableData[0]?.packages.length ?? 0);
    const columnMeta: ColumnSettings[] = [
        {data: "categoryName"},
        {data: "categoryGlNumber"},
    ];

    for (let i = 0; i < colLength; i++) {
        columnMeta.push(
            {data: `packages.${i}.amount`},
            {data: `packages.${i}.delay`},
        );
    }

    return columnMeta;
}

// calculates and sets configuration options for table rendering
export function refreshCellsConfig(isFiltered: boolean, hot: Handsontable): void {
    const tableConfig = new VZHotTableConfig();
    const columnCount = hot.countCols();
    const rowCount = hot.countRows();

    const renoPackageNamesRow = 0;
    const columnHeadersRow = 1;
    const costCategoryTotalsRow = rowCount - 2;
    const renoPackagePremiumRow = rowCount - 1;

    tableConfig.applyToColumnAndFollowing(0, {isBoldText: true});
    tableConfig.applyToColumnAndFollowing(2, {isReadOnly: false, isBoldText: false});

    if (!isFiltered) {
        tableConfig.applyToRow(renoPackageNamesRow, {isReadOnly: true});
        tableConfig.applyToRow(costCategoryTotalsRow, {format: "string", isReadOnly: true, isBoldText: true, isGreyBackground: true, isSelectable: false});
    }

    for (let colIndex = 0; colIndex < columnCount; colIndex ++) {
        const isEvenCol = colIndex % 2 == 0;

        // formatting for amount and delay columns
        if (colIndex > 1) {
            if (isEvenCol) {
                tableConfig.applyToColumn(colIndex, {format: "currency"});
            } else {
                tableConfig.applyToColumn(colIndex, {format: (cellProperties) => {
                    cellProperties.renderer = (
                        instance,
                        TD,
                        row,
                        col,
                        prop,
                        value,
                        cellProperties
                    ) => {
                        let valueToDisplay = null;

                        if (value === null || +value === 0) {
                            valueToDisplay = "None";
                        } else {
                            valueToDisplay = `${value} Month${+value === 1 ? "" : "s"}`;
                        }

                        Handsontable.renderers.NumericRenderer(
                            instance,
                            TD,
                            row,
                            col,
                            prop,
                            valueToDisplay,
                            cellProperties
                        );
                    };
                }});
            }
        }

        // settings for the first row with package names and remove button
        tableConfig.applyConfig({
            rowStart: 0,
            rowEnd: 0,
            colStart: colIndex,
            colEnd: colIndex,
            config: {
                isReadOnly: (colIndex > 0 && isEvenCol) ? false : true,
                hideBorderRight: isEvenCol,
                format: !isEvenCol ? "html" : "string",
                isSelectable: colIndex > 0 && isEvenCol ? true : false,
            }
        });

        if (!isEvenCol) {
            tableConfig.applyToColumn(colIndex, {isThickBorderRight: true});

            if (!isFiltered) {
                // remove formatting of delay recognition columns for the totals row
                tableConfig.applyConfig({
                    rowStart: costCategoryTotalsRow,
                    rowEnd: costCategoryTotalsRow,
                    colStart: colIndex,
                    colEnd: colIndex,
                    config: {
                        format: "string",
                    }
                });
            }
        }
    }

    if (!isFiltered) {
        // removing right border from first column of bottom two rows
        tableConfig.applyConfig({
            rowStart: costCategoryTotalsRow,
            rowEnd: renoPackagePremiumRow,
            colStart: 0,
            colEnd: 0,
            config: {hideBorderRight: true}
        });
    }

    tableConfig.applyToRow(columnHeadersRow, {format: "string", isReadOnly: true, isBoldText: true, isGreyBackground: true, isSelectable: false});

    hot.updateSettings({cells: vzHotTableCells(tableConfig)});
    hot.render();
}

export function getNewPackageName(
    tableData: RenovationsPackageTableRow[],
    newPackageCounter: MutableRefObject<number>,
    packageNameIdMap: Map<string, string>,
    name?: string,
): string {
    const currentNames = Array.from(packageNameIdMap.values());
    let newName = name ?? `New Package ${newPackageCounter.current}`;

    if (currentNames.includes(newName)) {
        newPackageCounter.current += 1;
        newName = `New Package ${newPackageCounter.current}`;
        return getNewPackageName(tableData, newPackageCounter, packageNameIdMap, newName);
    }

    return newName;
}

export function addPackage(
    tableData: RenovationsPackageTableRow[],
    newPackageCounter: MutableRefObject<number>,
    packageNameIdMap: MutableRefObject<Map<string, string>>,
): RenovationsPackageTableRow[] {
    const newName = getNewPackageName(tableData, newPackageCounter, packageNameIdMap.current);
    const tempId = "new-package" + uuid();
    packageNameIdMap.current.set(tempId, newName);

    for (const [index, tableRow] of Array.from(tableData.entries())) {
        if (index == 0) {
            tableRow.packages.push({
                id: tempId,
                name: newName,
                amount: newName,
                delay: packageRemovalButtonMarkup,
                originalAmount: null,
                originalDelay: null,
                originalName: newName,
            });
        } else if (index == 1) {
            tableRow.packages.push({
                id: tempId,
                name: newName,
                amount: "Amount",
                delay: "Delay Recognition",
                originalAmount: null,
                originalDelay: null,
                originalName: newName,
            });
        } else {
            tableRow.packages.push({
                id: tempId,
                name: newName,
                amount: null,
                delay: null,
                originalAmount: null,
                originalDelay: null,
                originalName: newName,
            });
        }
    }

    return tableData;
}

export function removePackage(
    tableData: RenovationsPackageTableRow[],
    colToRemove: number,
    packageNameIdMap: MutableRefObject<Map<string, string>>,
): RenovationsPackageTableRow[] {
    // get the index from the packages array that corresponds to the column clicked
    const packageIndex = (colToRemove - 3) / 2;
    const packageId = tableData[0]?.packages[packageIndex]?.id;

    if (packageId) {
        packageNameIdMap.current.delete(packageId);
    }

    for (const tableRow of tableData) {
        tableRow.packages.splice(packageIndex, 1);
    }

    return tableData;
}

export function getChanges(
    newData: RenovationsPackageTableRow[],
    existingPackageIds: Set<string>,
    packageNameIdMap: Map<string, string>,
): PackageChangesReturn {
    const newPackages: PackageUpdateMap = new Map();
    const updatedPackages: PackageUpdateMap = new Map();

    const valueToNullOrString = (value: number | string | null): string | null => {
        if (value === null || typeof value == undefined) {
            return null;
        }
        return value.toString();
    };

    for (const category of newData) {
        for (const renoPackage of category.packages) {
            const packageName = packageNameIdMap.get(renoPackage.id) ?? renoPackage.name;
            const amount = valueToNullOrString(renoPackage.amount);
            const delay = valueToNullOrString(renoPackage.delay);
            const originalAmount = valueToNullOrString(renoPackage.originalAmount);
            const originalDelay = valueToNullOrString(renoPackage.originalDelay);

            existingPackageIds.delete(renoPackage.id);

            // ignore rows that just provide column headers or totals
            if (category.categoryId == "col-header-row" || category.categoryId == "totals") {
                continue;
            } else if (renoPackage.id.startsWith("new-package")) {
                let newPackage = newPackages.get(renoPackage.id);

                if (!newPackage) {
                    newPackage = {
                        id: renoPackage.id,
                        name: packageName,
                        costCategoryMappings: [],
                        premium: null,
                        premiumDelayRecognition: null,
                    };

                    newPackages.set(renoPackage.id, newPackage);
                }
                // ignore cost categories where both amount and delay are null, for new packages
                if (amount === null && delay === null) {
                    continue;
                }

                if (category.categoryId == "premium") {
                    newPackage.premium = amount == null ? null : amount;
                    newPackage.premiumDelayRecognition = delay == null ? null : delay;
                } else {
                    newPackage.costCategoryMappings.push({
                        costCategoryId: category.categoryId,
                        amount: amount,
                        delayRecognition: delay,
                    });
                }
            // ignore updating existing packages if new values match old values
            } else if (packageName !== renoPackage.originalName || amount !== originalAmount || delay !== originalDelay) {
                let updatedPackage = updatedPackages.get(renoPackage.id);

                if (!updatedPackage) {
                    updatedPackage = {
                        id: renoPackage.id,
                        name: packageName,
                        costCategoryMappings: [],
                        premium: null,
                        premiumDelayRecognition: null,
                    };

                    updatedPackages.set(renoPackage.id, updatedPackage);
                }

                if (amount !== originalAmount || delay !== originalDelay) {
                    if (category.categoryId == "premium") {
                        updatedPackage.premium = amount == null ? null : amount;
                        updatedPackage.premiumDelayRecognition = delay == null ? null : delay;
                    } else {
                        updatedPackage.costCategoryMappings.push({
                            costCategoryId: category.categoryId,
                            amount: amount,
                            delayRecognition: delay,
                        });
                    }
                }
            }
        }
    }

    return {
        packagesToCreate: newPackages,
        packagesToUpdate: updatedPackages,
        packagesToDelete: existingPackageIds,
    };
}

export function summarizeColTotals(colIndexes: number[], hot: Handsontable): void {
    const rowCount = hot.countRows();
    const updatesToMake: [number, number, number][] = [];

    if (rowCount == 0) {
        return;
    }

    for (const colIndex of colIndexes) {
        const colValues = hot.getDataAtCol(colIndex).map(x => x == null ? 0 : +x);
        if (colValues.length == 0) {
            continue;
        }
        const costCategoryValues = colValues.slice(2, rowCount - 2);
        const costCategoryTotal = costCategoryValues.sum();
        updatesToMake.push([rowCount - 2, colIndex, costCategoryTotal]);
    }

    hot.setDataAtCell(updatesToMake, "calculatedColTotal");
}

export function summarizeAllColTotals(hot: Handsontable): void {
    const colCount = hot.countCols();
    const allTotalsColIndexes: number[] = [];

    if (colCount == 0) {
        return;
    }

    for (let colIndex = 0; colIndex < colCount; colIndex++) {
        const isEvenCol = colIndex % 2 == 0;

        if (isEvenCol && colIndex > 1) {
            allTotalsColIndexes.push(colIndex);
        }
    }

    if (allTotalsColIndexes.length > 0) {
        summarizeColTotals(allTotalsColIndexes, hot);
    }
}
